import React, { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Autocomplete
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import authApi from "../Service/Auth.api";
import { setAlertData, startLoading, stopLoading } from "../Redux/Screen/Action";
import OtpInput from "./OtpInput";

import { setMyInfo } from "../Redux/User/Action";
import { FormateMyInfoData } from "../Pages/Login/Formatter";

interface GoogleAuthButtonProps {
  userType?: string; // "USER" | "PROFESSIONAL" | "CORPORATE" | "NRI"
}

const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({ userType = "USER" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Selected Role state for onboarding modal
  const [selectedRole, setSelectedRole] = useState(userType);
  const [orgList, setOrgList] = useState<any[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<any>(null);

  React.useEffect(() => {
    if (selectedRole === "CORPORATE" && orgList.length === 0) {
      authApi.getOrganizations().then((res: any) => {
        if (res?.data && Array.isArray(res.data)) {
          setOrgList(res.data);
        }
      }).catch((err: any) => {
        console.error("Failed to load organizations:", err);
      });
    }
  }, [selectedRole, orgList.length]);

  // Mobile Modal States
  const [openModal, setOpenModal] = useState(false);
  const [preAuthToken, setPreAuthToken] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);

  const startResendTimer = () => {
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const showAlert = (message: string, variant: "error" | "success" | "info" | "warning") => {
    dispatch(setAlertData({ alertMessage: message, alertVariant: variant, openAlert: true }));
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    if (!credentialResponse?.credential) {
      showAlert("Google Sign-In failed. Try again.", "error");
      return;
    }

    try {
      dispatch(startLoading({ loading: true, loadtext: "Signing in with Google..." }));
      const res: any = await authApi.googleLogin({
        idToken: credentialResponse.credential,
        userType: userType
      });
      dispatch(stopLoading());

      const data = res?.data || res;

      if (data?.status === "SUCCESS") {
        localStorage.setItem("user", JSON.stringify(data));
        localStorage.setItem("token", data.token);
        localStorage.setItem("x-nlp-userid", String(data.id));
        dispatch(setMyInfo(FormateMyInfoData(data)) as any);
        showAlert("Logged in successfully!", "success");
        navigate("/dashboard", { replace: true });
      } else if (data?.status === "REQUIRES_MOBILE_VERIFICATION") {
        setPreAuthToken(data.preAuthToken);
        setOpenModal(true);
      } else {
        showAlert(data?.message || res?.alert?.alertMessage || "Google Sign-In failed", "error");
      }
    } catch (err: any) {
      dispatch(stopLoading());
      showAlert(err?.response?.data?.Message || "Google login failed", "error");
    }
  };

  const handleSendMobileOtp = async () => {
    if (!/^\d{10}$/.test(mobileNumber.trim())) {
      showAlert("Please enter a valid 10-digit mobile number.", "error");
      return;
    }

    if (selectedRole === "CORPORATE" && !selectedOrg) {
      showAlert("Please select your organization.", "error");
      return;
    }

    try {
      setLoading(true);
      const res: any = await authApi.googleRequestMobileOtp({
        preAuthToken: preAuthToken,
        mobileNumber: mobileNumber.trim()
      });
      setLoading(false);

      if (res?.success !== false && res?.status !== "ERROR" && res?.data !== "User with this mobile number already exists.") {
        setOtpSent(true);
        startResendTimer();
        showAlert("OTP sent successfully to your mobile number!", "success");
      } else {
        const errorMsg = typeof res?.data === "string" ? res.data : (res?.alert?.alertMessage || res?.message || "Failed to send OTP");
        showAlert(errorMsg, "error");
      }
    } catch (err: any) {
      setLoading(false);
      const errorMsg = typeof err?.response?.data === "string" 
        ? err.response.data 
        : err?.response?.data?.message || err?.response?.data?.Message || err?.message || "Failed to send OTP";
      showAlert(errorMsg, "error");
    }
  };

  const handleVerifyAndComplete = async () => {
    if (otp.length < 4) {
      showAlert("Please enter a valid 4-digit OTP.", "error");
      return;
    }

    try {
      setLoading(true);
      const res: any = await authApi.googleCompleteOnboarding({
        preAuthToken: preAuthToken,
        mobileNumber: mobileNumber.trim(),
        otp: otp,
        userType: selectedRole,
        corporateOrg: selectedRole === "CORPORATE" 
          ? (typeof selectedOrg === "string" 
              ? selectedOrg 
              : String(selectedOrg?.value || selectedOrg?.id || selectedOrg?.PkCorporateOrgs || selectedOrg?.label || selectedOrg?.OrgName || "")) 
          : undefined
      });
      setLoading(false);

      const data = res?.data || res;

      if (data?.status === "SUCCESS") {
        localStorage.setItem("user", JSON.stringify(data));
        localStorage.setItem("token", data.token);
        localStorage.setItem("x-nlp-userid", String(data.id));
        dispatch(setMyInfo(FormateMyInfoData(data)) as any);
        setOpenModal(false);
        showAlert("Registration & Profile Completion Successful!", "success");
        navigate("/dashboard", { replace: true });
      } else {
        showAlert(data?.message || res?.alert?.alertMessage || "Verification failed", "error");
      }
    } catch (err: any) {
      setLoading(false);
      showAlert(err?.response?.data || "Verification failed", "error");
    }
  };

  return (
    <Box sx={{ width: "100%", my: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box sx={{ width: "100%", maxWidth: 360 }}>
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => {
            showAlert("Google Sign-In failed", "error");
          }}
          useOneTap
          shape="pill"
          size="large"
          theme="outline"
          text="continue_with"
        />
      </Box>

      {/* Single Input Mobile Verification Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="bold">
            Complete Your Profile
          </Typography>
          <IconButton onClick={() => setOpenModal(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ py: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PhoneIphoneIcon color="primary" />
              <Typography variant="body2" color="textSecondary">
                Enter your mobile number to complete your profile verification.
              </Typography>
            </Box>

            {!otpSent && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="textSecondary">
                  Select Account Type:
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {[
                    { label: "Individual", value: "USER" },
                    { label: "Professional", value: "PROFESSIONAL" },
                    { label: "Corporate", value: "CORPORATE" },
                    { label: "NRI", value: "NRI" }
                  ].map((role) => (
                    <Chip
                      key={role.value}
                      label={role.label}
                      clickable
                      color={selectedRole === role.value ? "primary" : "default"}
                      variant={selectedRole === role.value ? "filled" : "outlined"}
                      onClick={() => setSelectedRole(role.value)}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {!otpSent && selectedRole === "CORPORATE" && (
              <Autocomplete
                freeSolo
                options={orgList}
                getOptionLabel={(option: any) =>
                  typeof option === "string"
                    ? option
                    : option?.label || option?.name || option?.OrgName || ""
                }
                value={selectedOrg}
                onChange={(_, newValue) => setSelectedOrg(newValue)}
                onInputChange={(_, newInputValue) => {
                  if (typeof selectedOrg !== "object" || !selectedOrg) {
                    setSelectedOrg(newInputValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    placeholder="Type or Select Organization"
                    variant="outlined"
                    fullWidth
                    required
                  />
                )}
              />
            )}

            <TextField
              label="Mobile Number"
              variant="outlined"
              fullWidth
              disabled={otpSent}
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="Enter 10 digit mobile number"
              inputProps={{ maxLength: 10 }}
            />

            {!otpSent ? (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading || mobileNumber.length !== 10}
                onClick={handleSendMobileOtp}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Send Verification OTP"}
              </Button>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Enter 4-digit OTP sent to +91 {mobileNumber}:
                </Typography>
                <OtpInput length={4} value={otp} onChange={(val: string) => setOtp(val)} />

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={loading || otp.length < 4}
                  onClick={handleVerifyAndComplete}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Verify OTP & Finish Onboarding"}
                </Button>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Button
                    size="small"
                    disabled={resendTimer > 0 || loading}
                    onClick={handleSendMobileOtp}
                  >
                    {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
                  </Button>

                  <Button
                    size="small"
                    color="secondary"
                    onClick={() => setOtpSent(false)}
                  >
                    Change Mobile
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GoogleAuthButton;
